import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Phone, Bell, Droplet, HeartPulse, AlertCircle, Loader2, ShieldCheck, X, Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TagPublicPage() {
  const { slug } = useParams();
  const [state, setState] = useState({ loading: true });
  const [notifyOpen, setNotifyOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/qr/public/tag/${slug}`)
      .then((r) => setState({ loading: false, data: r.data }))
      .catch((e) => setState({ loading: false, error: e.response?.data?.detail || 'Not found' }));
  }, [slug]);

  if (state.loading) return <Center><Loader2 className="animate-spin" /> Loading emergency profile…</Center>;
  if (state.error) return <Center><X size={40} className="text-red-500" /><h1 className="h-display text-2xl mt-2">Tag not found</h1><p className="text-sm text-neutral-500 mt-1">{state.error}</p></Center>;

  const d = state.data;
  if (d.status !== 'active') return <InactiveTag d={d} />;

  return (
    <div className="min-h-screen bg-gradient-to-b from-red-50 to-white" data-testid="tag-public-page">
      <div className="bg-red-600 text-white text-center py-3 font-bold text-sm tracking-wider">🚨 EMERGENCY VEHICLE PROFILE</div>
      <div className="max-w-md mx-auto px-4 py-6 space-y-4">
        <div className="bg-white rounded-2xl p-5 shadow-lg border border-red-100" data-testid="vehicle-card">
          {d.vehicle_photo && <img src={d.vehicle_photo} alt="Vehicle" className="w-full rounded-lg mb-3 object-cover max-h-56" data-testid="vehicle-photo" />}
          <p className="text-[10px] uppercase text-neutral-400 tracking-widest">Vehicle</p>
          <h1 className="h-display text-3xl mt-1">{d.vehicle.make} {d.vehicle.model}</h1>
          <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
            <span className="bg-neutral-900 text-white font-mono font-bold px-3 py-1.5 rounded" data-testid="reg-plate">{d.vehicle.registration_number}</span>
            <span className="text-neutral-600">· {d.vehicle.colour}</span>
            <span className="text-neutral-400 text-xs">· {d.vehicle.type}</span>
          </div>
          <p className="mt-3 text-xs text-neutral-500">Owner: <span className="font-semibold text-neutral-800">{d.owner_name_masked}</span></p>
        </div>

        {d.blood_group && (
          <Card icon={Droplet} title="Blood Group" body={<p className="h-display text-4xl text-red-600" data-testid="blood-group">{d.blood_group}</p>} />
        )}

        {d.medical && (d.medical.allergies || d.medical.medical_conditions || d.medical.important_medical_info || d.medical.emergency_notes) && (
          <Card icon={HeartPulse} title="Medical Information" body={
            <div className="text-sm space-y-2" data-testid="medical-info">
              {d.medical.allergies && <p><span className="font-semibold text-red-700">Allergies:</span> {d.medical.allergies}</p>}
              {d.medical.medical_conditions && <p><span className="font-semibold text-red-700">Conditions:</span> {d.medical.medical_conditions}</p>}
              {d.medical.important_medical_info && <p><span className="font-semibold text-red-700">Important:</span> {d.medical.important_medical_info}</p>}
              {d.medical.emergency_notes && <p className="text-neutral-600 italic">"{d.medical.emergency_notes}"</p>}
            </div>
          } />
        )}

        {d.emergency_contact && (
          <Card icon={AlertCircle} title="Emergency Contact" body={
            <p className="text-sm" data-testid="ec-info"><span className="font-semibold">{d.emergency_contact.name}</span>{d.emergency_contact.relationship ? ` (${d.emergency_contact.relationship})` : ''}</p>
          } />
        )}

        <div className="space-y-3 pt-2">
          <CallOwner slug={slug} />
          <button onClick={() => setNotifyOpen(true)} className="w-full bg-white border-2 border-navy text-navy font-bold rounded-2xl py-4 flex items-center justify-center gap-2 hover:bg-navy hover:text-white transition-colors" data-testid="notify-btn">
            <Bell size={18} /> NOTIFY OWNER
          </button>
        </div>

        <p className="text-[10px] text-center text-neutral-400 mt-6">Powered by Connect Near Me · Privacy-first emergency tags</p>
      </div>

      {notifyOpen && <NotifyModal slug={slug} onClose={() => setNotifyOpen(false)} />}
    </div>
  );
}

function InactiveTag({ d }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const verify = async () => {
    setBusy(true); setErr('');
    try {
      await axios.post(`${API}/qr/activate/verify-code`, { tag_id: d.tag_id, activation_code: code.trim() });
      navigate(`/activate?tag=${d.tag_id}&code=${code.trim()}`);
    } catch (e) { setErr(e.response?.data?.detail || 'Invalid activation code'); } finally { setBusy(false); }
  };
  return (
    <Center>
      <div data-testid="tag-inactive" className="w-full max-w-sm">
        <ShieldCheck size={44} className="text-navy mx-auto" />
        <h1 className="h-display text-2xl mt-3">Not activated yet</h1>
        <p className="text-sm text-neutral-600 mt-2">{d.message}</p>
        <p className="font-mono text-xs text-neutral-400 mt-1" data-testid="inactive-tag-id">{d.tag_id}</p>
        {d.can_activate && !open && (
          <button onClick={() => setOpen(true)} className="btn-primary w-full justify-center mt-6" data-testid="activate-now-btn">Activate Now</button>
        )}
        {d.can_activate && open && (
          <div className="mt-6 text-left space-y-3" data-testid="activate-code-form">
            <label className="block text-[11px] uppercase tracking-wide text-neutral-500">Activation code
              <input autoFocus value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} maxLength={8} placeholder="XXXXXX" className="mt-1 w-full px-3 py-3 rounded-lg border border-black/15 font-mono tracking-widest text-center text-lg focus:outline-none focus:ring-2 focus:ring-navy/30" data-testid="scan-activation-code-input" />
            </label>
            <p className="text-[11px] text-neutral-500">Find it in My Orders on your Connect Near Me account, or in your tag pack.</p>
            {err && <p className="text-xs text-red-600" data-testid="scan-activation-error">{err}</p>}
            <button onClick={verify} disabled={busy || code.trim().length < 4} className="btn-primary w-full justify-center disabled:opacity-60" data-testid="scan-activation-continue-btn">{busy ? 'Verifying…' : 'Continue'}</button>
          </div>
        )}
      </div>
    </Center>
  );
}

function CallOwner({ slug }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [phone, setPhone] = useState('');
  const [ask, setAsk] = useState(false);
  const call = async () => {
    if (!ask) { setAsk(true); return; }
    setBusy(true); setMsg('');
    try {
      const { data } = await axios.post(`${API}/qr/public/tag/${slug}/call`, { caller_phone: phone });
      setMsg(data.note);
    } catch (e) { setMsg(e.response?.data?.detail || 'Call failed'); } finally { setBusy(false); }
  };
  return (
    <div>
      {ask && (
        <div className="mb-2">
          <input autoFocus type="tel" inputMode="numeric" maxLength={13} value={phone} onChange={(e) => setPhone(e.target.value.replace(/[^\d+]/g, ''))} placeholder="Your mobile number" className="w-full px-4 py-3 rounded-xl border border-black/15 text-center tracking-wider focus:outline-none focus:ring-2 focus:ring-red-300" data-testid="caller-phone-input" />
          <p className="text-[11px] text-neutral-500 text-center mt-1">We'll call you and connect you to the owner. Neither number is shared.</p>
        </div>
      )}
      <button onClick={call} disabled={busy || (ask && phone.replace(/\D/g, '').length < 10)} className="w-full bg-red-600 text-white font-bold rounded-2xl py-4 flex items-center justify-center gap-2 hover:bg-red-700 transition-colors disabled:opacity-60" data-testid="call-btn">
        <Phone size={18} /> {busy ? 'Connecting…' : ask ? 'CALL ME NOW' : 'CALL OWNER'}
      </button>
      {msg && <p className="text-xs text-center mt-2 text-navy" data-testid="call-msg">{msg}</p>}
    </div>
  );
}

function NotifyModal({ slug, onClose }) {
  const [form, setForm] = useState({ scanner_name: '', scanner_phone: '', message: '' });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');
  const send = async () => {
    setBusy(true); setErr('');
    try {
      await axios.post(`${API}/qr/public/tag/${slug}/notify`, form);
      setDone(true);
    } catch (e) { setErr(e.response?.data?.detail || 'Failed'); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-3" onClick={onClose} data-testid="notify-modal">
      <div className="bg-white rounded-2xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-3">
          <h3 className="font-semibold">Notify the owner</h3>
          <button onClick={onClose} className="p-1"><X size={18} /></button>
        </div>
        {done ? (
          <div className="text-center py-6">
            <div className="w-14 h-14 rounded-full bg-green-100 text-green-600 mx-auto flex items-center justify-center mb-2"><Send size={22} /></div>
            <p className="font-semibold">Notification sent</p>
            <p className="text-xs text-neutral-500 mt-1">The owner has been alerted with your message.</p>
            <button onClick={onClose} className="btn-primary mt-4">Close</button>
          </div>
        ) : (
          <>
            <p className="text-xs text-neutral-500 mb-3">All fields are optional. Your info is only shared with the owner.</p>
            <input value={form.scanner_name} onChange={(e) => setForm({ ...form, scanner_name: e.target.value })} placeholder="Your name (optional)" className="w-full px-3 py-2 rounded-lg border border-black/15 text-sm mb-2" data-testid="notify-name" />
            <input value={form.scanner_phone} onChange={(e) => setForm({ ...form, scanner_phone: e.target.value })} placeholder="Your phone (optional)" className="w-full px-3 py-2 rounded-lg border border-black/15 text-sm mb-2" data-testid="notify-phone" />
            <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={3} placeholder="Message (e.g. Your car is blocking my gate)" className="w-full px-3 py-2 rounded-lg border border-black/15 text-sm resize-none mb-3" data-testid="notify-message" />
            {err && <p className="text-xs text-red-600 mb-2">{err}</p>}
            <button onClick={send} disabled={busy} className="btn-primary w-full justify-center disabled:opacity-60" data-testid="notify-send-btn">{busy ? 'Sending…' : 'Send notification'}</button>
          </>
        )}
      </div>
    </div>
  );
}

const Card = ({ icon: Icon, title, body }) => (
  <div className="bg-white rounded-2xl p-4 shadow-sm border border-black/5">
    <div className="flex items-center gap-2 mb-2"><Icon size={14} className="text-red-600" /><p className="text-[10px] uppercase tracking-widest text-neutral-400 font-semibold">{title}</p></div>
    {body}
  </div>
);

const Center = ({ children }) => <div className="min-h-screen flex flex-col items-center justify-center text-center px-6">{children}</div>;
